import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import { uniq, uniqBy } from 'lodash';
import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import IconButton from 'components/common/IconButton';
import Badge from 'components/common/Badge';
import IconArrowHeadBottom from 'components/icons/IconArrowHeadBottom';
import IconRefresh from 'components/icons/IconRefresh';
import IconInfo from 'components/icons/IconInfo';
import IconWarning from 'components/icons/IconWarning';
import IconDocument from 'components/icons/IconDocument';
import Link from 'components/common/Link';
import Menu, { MenuItem } from 'components/common/Menu';

import formatPrice from 'utils/formatPrice';
import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';
import formatOrderNumber from 'utils/formatOrderNumber';

import pxToRem from 'styles/pxToRem';

import dateFormat from 'config/dateFormat';
import timeDateFormat, { dateTimeFormat} from 'config/timeDateFormat';

import { refreshClaimStatus, downloadExplainClaim } from 'redux/modules/claims';
import { getEraDocument } from 'redux/modules/eraStatistics';

import AttachmentsDialog from 'components/reports/AttachmentsDialog';
import DocumentsViewDialog from 'components/common/DocumentsViewDialog';
import ClaimsTableRowItem from './ClaimsTableRowItem';

const finalizedClaimStatuses = [
  'Claim Create',
  'System Validation Fail',
  'Validation Request Fail',
  'Submission Request Fail',
  'Status Request Validation Fail',
  'Status Response Validation Fail',
  'Status Unexpect Fail',
  'Not Support Claim Create',
  'Not Support Claim Status',
  'Finalized',
  'Requests for additional information',
  'Error',
  'Searches',
];

const styles = theme => ({
  buttonCell: {
    padding: '0 10px 0 0',
    '&:last-child': {
      padding: '0 10px 0 0',
    },
  },
  iconArrowHeadBottom: {
    width: 12,
    height: 8,
  },
  iconArrowHeadBottomExpanded: {
    transform: 'rotate(180deg)',
  },
  buttonExpand: {
    margin: -14,
  },
  rowFullWidth: {
    height: 'auto',
  },
  cellFullWidth: {
    padding: 0,
    border: 'none',
    '&:last-child': {
      padding: 0,
    },
  },
  details: {
    display: 'block',
    padding: 0,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
    background: theme.palette.grey[100],
  },
  cellInsurance: {
    whiteSpace: 'nowrap',
  },
  expansionPanelClickable: {
  },
  iconRefresh: {
    width: 12,
    height: 12,
  },
  iconRefreshLoading: {
    color: theme.palette.primary[500],
  },
  buttonRefresh: {
    position: 'relative',
    top: 1,
    margin: '-18px -18px -18px -12px',
  },
  iconInfo: {
    width: 19,
    height: 19,
  },
  iconDocument: {
    width: 19,
    height: 19,
  },
  button: {
    margin: -8,
  },
  insuranceName: {
    whiteSpace: 'normal',
  },
  cellStatus: {
    textTransform: 'capitalize',
    fontWeight: 'bold',
  },
  cellStatusSent: {
    color: theme.palette.notification['500'],
  },
  cellStatusSuccess: {
    color: theme.palette.add['500'],
  },
  cellStatusError: {
    color: theme.palette.validation['500'],
  },
  cellError: {
    position: 'relative',
    backgroundColor: theme.palette.validation[100],
    color: theme.palette.validation[500],
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
    padding: '16px 16px 16px 54px',
    '&:last-child': {
      padding: '16px 16px 16px 54px',
    },
    '&:first-child': {
      padding: '16px 16px 16px 54px',
    },
  },
  cellAdjustment: {
    backgroundColor: theme.palette.primary.A100,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
    padding: '16px 16px 16px 54px',
    '&:last-child': {
      padding: '4px 16px 16px 24px',
    },
    '&:first-child': {
      padding: '4px 16px 16px 24px',
    },
  },
  cellAdjustmentHead: {
    backgroundColor: theme.palette.primary.A100,
    padding: '16px 16px 16px 54px',
    fontSize: 12,
    letterSpacing: '0.03em',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: theme.palette.grey[600],
    '&:last-child': {
      padding: '16px 16px 4px 24px',
    },
    '&:first-child': {
      padding: '16px 16px 4px 24px',
    },
  },
  iconWarning: {
    position: 'absolute',
    top: '50%',
    left: 16,
    marginTop: -10,
    width: 24,
    height: 21,
    marginRight: 16,
    verticalAlign: 'middle',
  },
  cellDetails: {
    overflow: 'hidden',
  },
  typographyOrderLinesCount: {
    display: 'inline-block',
    fontSize: pxToRem(13),
    width: 10,
  },
  cellSubmitDate: {
    width: 79,
  },
  iconEra: {
    position: 'relative',
    display: 'inline-block',
    verticalAlign: 'middle',
    marginLeft: 4,
    marginBottom: -3,
    marginTop: -5,
  },
  iconEraDocument: {
    width: 18,
    height: 18,
    color: theme.palette.grey[600],
  },
  iconEraText: {
    display: 'block',
    position: 'absolute',
    paddingLeft: 2,
    paddingRight: 2,
    bottom: 0,
    right: 0,
    fontSize: pxToRem(7),
    fontWeight: 700,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary[500],
    textShadow: 'none',
  },
  cellCount: {
    borderRight: `1px solid ${theme.palette.primary.A100}`,
    paddingRight: 0,
  },
  cellOrderNumber: {
    paddingLeft: 8,
  },
  eraItem: {
    '& + $eraItem': {
      marginTop: 6,
    },
  },
  linkEra: {
    whiteSpace: 'nowrap',
  },
});

class СlaimsTableRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      expanded: false,
      refreshIconAngle: 0,
      attachmentsDialogOpen: false,
      attachmentViewDialogOpen: false,
      viewAttachment: null,
      anchorEl: null,
    };

    this.onTableRowClick = this.onTableRowClick.bind(this);
    this.onExpandButtonClick = this.onExpandButtonClick.bind(this);
    this.onRefreshClaimStatusButtonClick = this.onRefreshClaimStatusButtonClick.bind(this);
    this.onAttachmentsButtonClick = this.onAttachmentsButtonClick.bind(this);
    this.onAttachmentsDialogClose = this.onAttachmentsDialogClose.bind(this);
    this.onAttachmentView = this.onAttachmentView.bind(this);
    this.onAttachmentViewDialogClose = this.onAttachmentViewDialogClose.bind(this);
    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
  }

  onTableRowClick(event) {
    const { classes } = this.props;
    if (event.target.classList.contains(classes.expansionPanelClickable)) {
      this.setState({ expanded: !this.state.expanded });
    }
  }

  onExpandButtonClick() {
    this.setState({ expanded: !this.state.expanded });
  }

  onRefreshClaimStatusButtonClick() {
    const { actions, claim } = this.props;
    actions.refreshClaimStatus({
      values: {
        'Claim ID': claim.ID,
      },
      resolve: () => {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
        this.setState({ refreshIconAngle: 0 });
      },
      reject: () => {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
        this.setState({ refreshIconAngle: 0 });
      },
    });
    this.refreshInterval = setInterval(() => {
      this.setState({ refreshIconAngle: this.state.refreshIconAngle + 10 });
    }, 50);
  }

  onAttachmentsButtonClick() {
    this.setState({ attachmentsDialogOpen: true });
  }

  onAttachmentsDialogClose() {
    this.setState({ attachmentsDialogOpen: false });
  }

  onAttachmentView(viewAttachment) {
    this.setState({
      attachmentsDialogOpen: false,
      attachmentViewDialogOpen: true,
      viewAttachment,
    });
  }

  onAttachmentViewDialogClose() {
    this.setState({ attachmentViewDialogOpen: false });
  }

  onMenuButtonClick(event) {
    this.setState({ anchorEl: event.currentTarget });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  render() {
    const { claim, business, classes } = this.props;
    const {
      expanded,
      refreshIconAngle,
      attachmentsDialogOpen,
      attachmentViewDialogOpen,
      viewAttachment,
      anchorEl,
    } = this.state;
    const open = Boolean(anchorEl);
    const charge = claim['Submitted Amount'] ? formatPrice(claim['Submitted Amount']) : '';
    const currentStatus = claim.Status && claim.Status.length > 0 ? claim.Status[claim.Status.length - 1] : null;
    const ERAs = claim['ERA Claim'] && claim['ERA Claim'].length > 0
      ? uniqBy(claim['ERA Claim'].reduce((accumulator, ERAClaimItem) => [
        ...accumulator,
        ...ERAClaimItem.ERA,
      ], []), 'ID')
      : [];
    return (
      <Fragment>
        <TableRow
          className={classes.expansionPanelClickable}
          onClick={this.onTableRowClick}
          hover
        >
          <TableCell className={classNames(classes.cellCount, classes.expansionPanelClickable)}>
            {(claim['Claim Line'] && claim['Claim Line'].length > 0) &&
              <Fragment>
                <Typography
                  className={classNames(classes.expansionPanelClickable,
                    classes.typographyOrderLinesCount)}
                >
                  {claim['Claim Line'].length}
                </Typography>
                <IconButton
                  className={classes.buttonExpand}
                  onClick={this.onExpandButtonClick}
                >
                  <IconArrowHeadBottom
                    className={classNames(classes.iconArrowHeadBottom, {
                      [classes.iconArrowHeadBottomExpanded]: expanded,
                    })}
                  />
                </IconButton>     
              </Fragment>
            }
          </TableCell>
          <TableCell className={classNames(classes.cellOrderNumber, classes.expansionPanelClickable)}>
            {claim['Order ID'] &&
              <Link
                href={`/workflow/order/edit/${claim['Order ID']}`}
                inlineLabel
                breakWord
                target="_blank"
              >
                {formatOrderNumber(claim['Order ID'])}
              </Link>     
            }
          </TableCell>
          <TableCell className={classNames(classes.expansionPanelClickable, classes.cellSubmitDate)}>
            {formatUtcToLocalDate(
              claim['Create At'],
              timeDateFormat,
              dateTimeFormat,
              business['Company Address']['Time Zone'],
              business['Company Address']['Observes Daylight Saving'],
            )}
          </TableCell>
          <TableCell
            className={classNames(classes.cellStatus, classes.expansionPanelClickable, {
              [classes.cellStatusSent]: currentStatus && currentStatus.Category === 'Sent',
              [classes.cellStatusSuccess]: currentStatus && currentStatus.Category === 'Success',
              [classes.cellStatusError]: currentStatus && currentStatus.Category === 'Error',
            })}
          >
            {claim['Claim Status']}
            {(claim['Claim Status'] && finalizedClaimStatuses.indexOf(claim['Claim Status']) === -1) &&
              <IconButton
                className={classes.buttonRefresh}
                onClick={this.onRefreshClaimStatusButtonClick}
                tooltipContent="Refresh Status"
              >
                <IconRefresh
                  className={classNames(classes.iconRefresh, {
                    [classes.iconRefreshLoading]: this.refreshInterval,
                  })}
                  style={{
                    transform: `rotate(${refreshIconAngle}deg)`,
                  }}
                />
              </IconButton>
            }
            {currentStatus ? currentStatus.Status : ''}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {claim.Patient['Record Number'] &&
              <Link
                href={`/charts/${claim.Patient['Record Number']}/profile`}
                inlineLabel
                breakWord
                target="_blank"
              >
                {`${claim.Patient['First Name']} ${claim.Patient['Last Name']}`}
              </Link>
            }
          </TableCell>
          <TableCell
            className={classNames(classes.cellInsurance, classes.expansionPanelClickable)}
          >
            {claim.Insurance['Payer Name'] &&
              <Fragment>
                <Badge
                  color="primary"
                  tooltipText={claim.Insurance['Order Benefits']}
                >
                  {claim.Insurance['Order Benefits'] === 'Primary' && '1'}
                  {claim.Insurance['Order Benefits'] === 'Secondary' && '2'}
                  {claim.Insurance['Order Benefits'] === 'Secondary As Primary' && '2'}
                  {claim.Insurance['Order Benefits'] === 'Tertiary' && '3'}
                </Badge>
                {' '}
                <span className={classes.insuranceName}>
                  {claim.Insurance['Payer Name']}
                </span>
              </Fragment>
            }
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {(claim['Order Line'] && claim['Order Line'].length > 0) &&
              (<Link
                href={`/workflow/order/edit/${claim['Order Line'][0]['Order ID']}`}
                inlineLabel
                breakWord
                target="_blank"
              >
                {uniq(claim['Order Line'].map(item => `${item['Date of Service From']}-${item['Date of Service To']}`))
                  .map(item => (
                    <div
                      key={item}
                      className={classNames(classes.dateOfService,
                        classes.expansionPanelClickable)}
                    >
                      {item}
                    </div>
                  ))}
              </Link>)}
            {(claim['Claim Line'] && claim['Claim Line'].length > 0 && claim['Claim Line'][0]['Date of Service From']) &&
              (<Link
                href={`/workflow/order/edit/${claim['Order ID']}`}
                inlineLabel
                breakWord
                target="_blank"
              >
                {uniq(claim['Claim Line'].map(item => `${item['Date of Service From']}-${item['Date of Service To']}`))
                  .map(item => (
                    <div
                      key={item}
                      className={classNames(classes.dateOfService,
                        classes.expansionPanelClickable)}
                    >
                      {item}
                    </div>
                  ))}
              </Link>)}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {charge}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {claim['ERA Claim'] && claim['ERA Claim'].length > 0
              ? formatPrice(claim['ERA Claim'].reduce((accumulator, ERAItem) => accumulator + (ERAItem.Paid || 0), 0)) : ''}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {(claim['Change Code'] && claim['Change Link']) &&
              <Link
                color="primary"
                href={claim['Change Link']}
                target="_blank"
                breakWord
              >
                {claim['Change Code']}
              </Link>
            }
            {(claim['Change Code'] && !claim['Change Link']) &&
              claim['Change Code']
            }
            {claim['Claim Md Code']}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {ERAs.length > 0
              ? ERAs.map(ERAItem => (
                <div key={ERAItem.ID} className={classes.eraItem}>
                  <Link
                    className={classes.linkEra}
                    onClick={() => {
                      this.props.actions.getEraDocument({
                        'ERA ID': ERAItem['ERA ID'],
                      });
                    }}
                    target="_blank"
                  >
                    {ERAItem['Check Number']}
                    <div className={classes.iconEra}>
                      <IconDocument className={classes.iconEraDocument} />
                      <span className={classes.iconEraText}>ERA</span>
                    </div>
                  </Link>
                </div>
              )) : ''}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {ERAs.length > 0
              ? ERAs.map(ERAItem => (
                <div key={ERAItem.ID} className={classes.eraItem}>
                  {moment(ERAItem['Paid Date'], 'MM/DD/YYYY').format(dateFormat)}
                </div>
              ))
              : ''}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {(claim['Send User'] && claim['Send User'].ID) &&
              `${claim['Send User']['First Name']} ${claim['Send User']['Last Name']}`}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {(claim.Attachment && claim.Attachment.length > 0) && (
              <Fragment>
                <IconButton
                  className={classes.button}
                  onClick={this.onAttachmentsButtonClick}
                  tooltipContent="Attachments"
                >
                  <IconDocument className={classes.iconDocument} />
                </IconButton>
                <AttachmentsDialog
                  open={attachmentsDialogOpen}
                  onClose={this.onAttachmentsDialogClose}
                  attachments={claim.Attachment}
                  onAttachmentView={this.onAttachmentView}
                />
                {viewAttachment &&
                  <DocumentsViewDialog
                    open={attachmentViewDialogOpen}
                    onClose={this.onAttachmentViewDialogClose}
                    Documents={[{
                      ...viewAttachment.cmn,
                    }]}
                    initialDocumentID={viewAttachment.cmn.ID}
                  />
                }
              </Fragment>
            )}
          </TableCell>
          <TableCell className={classNames(classes.cellDetails, classes.expansionPanelClickable)}>
            <IconButton
              className={classes.button}
              tooltipContent="Claim Details"
              aria-label="More"
              aria-owns={open ? `${claim.ID}-explain-menu` : null}
              aria-haspopup="true"
              onClick={this.onMenuButtonClick}
            >
              <IconInfo className={classes.iconInfo} />
            </IconButton>
            <Menu
              id={`${claim.ID}-explain-menu`}
              anchorEl={anchorEl}
              open={open}
              onClose={this.onMenuClose}
            >
              <MenuItem
                onClick={() => {
                  this.props.actions.downloadExplainClaim({
                    ID: claim.ID,
                    fileName: `Claim-${claim.ID}-Explain-Short`,
                  });
                }}
              >
                Short
              </MenuItem>
              <MenuItem
                onClick={() => {
                  this.props.actions.downloadExplainClaim({
                    ID: claim.ID,
                    fileName: `Claim-${claim.ID}-Explain-Full`,
                    full: true,
                  });
                }}
              >
                Full
              </MenuItem>
            </Menu>
          </TableCell>
        </TableRow>
        {(claim['Claim Line'] && claim['Claim Line'].length > 0 && expanded) &&
          <TableRow className={classes.rowFullWidth}>
            <TableCell
              colSpan="15"
              className={classes.cellFullWidth}
            >
              <div className={classes.details}>
                <Table>
                  <TableBody>
                    {claim['Claim Line'].map((item, index) =>
                      <ClaimsTableRowItem
                        key={`${item.ID}-details`}
                        showHead={index === 0}
                        {...item}
                        {...{'Order ID': claim['Order ID']}}
                      />)}
                  </TableBody>
                </Table>
              </div>
            </TableCell>
          </TableRow>
        }
        {claim['ERA Claim']
        && claim['ERA Claim'].length > 0 
        && (claim['ERA Claim'][0]['Claim Adjustment'].length > 0 || claim['ERA Claim'][0]['Provider Adjustment'].length > 0)
        && expanded &&
          <Fragment>
            <TableRow className={classes.rowFullWidth}>
              <TableCell
                colSpan="15"
                className={classNames(classes.cellFullWidth, classes.cellAdjustmentHead)}
              >
                Claim Adjustment
              </TableCell>
            </TableRow>
            <TableRow className={classes.rowFullWidth}>
              <TableCell
                colSpan="15"
                className={classNames(classes.cellFullWidth, classes.cellAdjustment)}
              >
                {claim['ERA Claim'] && claim['ERA Claim'].length > 0 && claim['ERA Claim'][0]['Claim Adjustment'].length > 0 &&
                  claim['ERA Claim'][0]['Claim Adjustment'].map(item => (
                    <div key={item.ID}>
                      {item.Group && item.Group.Code &&
                        <span>{`${item.Group.Code} - `}</span>
                      }
                      {item.Code.Code}: {item.Code.Description}
                    </div>
                ))}
                {claim['ERA Claim'] && claim['ERA Claim'].length > 0 && claim['ERA Claim'][0]['Provider Adjustment'].length > 0 &&
                  claim['ERA Claim'][0]['Provider Adjustment'].map(item => (
                    <div key={item.ID}>
                      {item.Group && item.Group.Code &&
                        <span>{`${item.Group.Code} - `}</span>
                      }
                      {item.Code.Code}: {item.Code.Description}
                    </div>
                ))}
              </TableCell>
            </TableRow>
          </Fragment>
        }
        {(claim.error && claim.error.length > 0 && expanded) &&
          <TableRow className={classes.rowFullWidth}>
            <TableCell
              colSpan="15"
              className={classNames(classes.cellFullWidth, classes.cellError)}
            >
              <IconWarning className={classes.iconWarning} />
              {claim.error.map(item => (
                <div key={item}>{item}</div>     
              ))}
            </TableCell>
          </TableRow>
        }
      </Fragment>
    );
  }
}

СlaimsTableRow.propTypes = {
  claim: PropTypes.shape({
    'Create At': PropTypes.string.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    refreshClaimStatus: PropTypes.func.isRequired,
    downloadExplainClaim: PropTypes.func.isRequired,
    getEraDocument: PropTypes.func.isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    buttonCell: PropTypes.string.isRequired,
    iconArrowHeadBottom: PropTypes.string.isRequired,
    iconArrowHeadBottomExpanded: PropTypes.string.isRequired,
    buttonExpand: PropTypes.string.isRequired,
    rowFullWidth: PropTypes.string.isRequired,
    cellFullWidth: PropTypes.string.isRequired,
    details: PropTypes.string.isRequired,
  }).isRequired,
};

СlaimsTableRow.defaultProps = {
  business: {},
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ refreshClaimStatus, downloadExplainClaim, getEraDocument }, dispatch),
});

export default connect(null, mapDispatchToProps)(
  withStyles(styles)(СlaimsTableRow),
);



// WEBPACK FOOTER //
// ./src/components/reports/ClaimsTable/ClaimsTableRow.js